import { useEffect, useRef, useState } from 'react'
import { Wind } from 'lucide-react'

const LAST_SYNC_KEY = 'cm.lastSyncAt'
/** Above this, drop the rolling timestamp — the cached data is too old to
 *  trust without saying so loudly. Spec'd at 30 min in offline-mode.md. */
const STALE_ESCALATION_MS = 30 * 60 * 1000
/** A persisted timestamp older than this on cold-start counts as unknown
 *  — overnight closures shouldn't make Monday morning say "hace 14 horas". */
const PERSISTED_VALID_MS = 12 * 60 * 60 * 1000

/**
 * Top-of-app offline indicator — shows when `navigator.onLine` is false
 * (probe-confirmed). Includes a rolling staleness timestamp ("Datos de hace
 * 12 min") so the receptionist knows whether the cached calendar can be
 * trusted.
 *
 * The "nueva versión disponible" update prompt that used to live here was
 * removed (2026-05-29) — version-update detection + the manual "Recargar"
 * affordance stay on the Novedades/Producto **Versión** section and the
 * admin health page; the top banner was redundant chrome. The SW still
 * picks up new bundles; `use-version-auto-reload` handles idle auto-reload.
 *
 * Kept small + classless so the host page's layout is unaffected. Hidden by
 * default (only appears when offline).
 */
export function AppStatusBanners() {
  const offline = useOffline()
  const lastSyncLabel = useStalenessLabel(offline)

  if (!offline) return null

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 14px',
        background: 'color-mix(in oklch, var(--warn) 22%, var(--panel-2))',
        color: 'var(--fg)',
        borderBottom: '1px solid var(--line)',
        fontSize: 'var(--fs)',
      }}
    >
      <Wind size={12} />
      <span>
        Sin conexión.{lastSyncLabel ? ` ${lastSyncLabel}.` : ''} Los cambios se
        guardarán al recuperar la red.
      </span>
    </div>
  )
}

/** Reliable offline detector. `navigator.onLine === false` is famously
 * unreliable — Chrome on macOS, corporate VPNs, captive-portal probes etc.
 * routinely flip it false on machines that are actually online. We use the
 * browser flag as a hint and confirm with a real fetch before showing the
 * banner. The probe target (`/manifest.webmanifest`) is the same-origin
 * PWA manifest so it works in both dev (Vite) and prod (Hosting) without
 * CORS noise.
 *
 * Side effect: while online, stamps `localStorage[LAST_SYNC_KEY]` every
 * 60 s so a subsequent offline transition has a known-good timestamp to
 * display, even after a cold reload. */
function useOffline(): boolean {
  const [offline, setOffline] = useState<boolean>(false)

  useEffect(() => {
    let cancelled = false
    const setSafe = (next: boolean) => {
      if (!cancelled) setOffline(next)
    }

    const probe = async () => {
      try {
        // `cache: 'no-store'` prevents the SW returning a stale 200 for a
        // genuinely offline state.
        const res = await fetch('/manifest.webmanifest', { cache: 'no-store' })
        setSafe(!res.ok)
      } catch {
        setSafe(true)
      }
    }

    const stampSync = () => {
      try {
        localStorage.setItem(LAST_SYNC_KEY, String(Date.now()))
      } catch {
        // private mode / quota — losing the timestamp is harmless.
      }
    }

    const handleOnline = () => {
      stampSync()
      setSafe(false)
    }
    const handleOffline = () => {
      // Don't trust the event blindly — confirm with a probe.
      void probe()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    if (
      typeof navigator !== 'undefined' &&
      'onLine' in navigator &&
      !navigator.onLine
    ) {
      void probe()
    } else {
      // Online at mount — stamp now and keep stamping every minute so the
      // staleness label has fresh data when we eventually drop offline.
      stampSync()
    }

    const stampInterval = setInterval(() => {
      if (
        typeof navigator !== 'undefined' &&
        'onLine' in navigator &&
        navigator.onLine
      ) {
        stampSync()
      }
    }, 60_000)

    return () => {
      cancelled = true
      clearInterval(stampInterval)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return offline
}

/** Human label for cache staleness while offline. Returns:
 *  - `null` if online, or if the persisted timestamp is missing / >12 h old.
 *  - `"Datos de hace X min"` for 1–29 minutes.
 *  - `"Datos de hace X minutos — conéctate para actualizar"` for 30+ minutes.
 *
 * Re-renders on a 30 s tick while offline so the label stays roughly current
 * without burning re-renders on the rest of the app. */
function useStalenessLabel(offline: boolean): string | null {
  const [, force] = useState(0)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!offline) {
      if (tickRef.current) clearInterval(tickRef.current)
      tickRef.current = null
      return
    }
    tickRef.current = setInterval(() => force((n) => n + 1), 30_000)
    return () => {
      if (tickRef.current) clearInterval(tickRef.current)
      tickRef.current = null
    }
  }, [offline])

  if (!offline) return null
  let raw: string | null = null
  try {
    raw = localStorage.getItem(LAST_SYNC_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  const ts = Number(raw)
  if (!Number.isFinite(ts)) return null
  const ageMs = Date.now() - ts
  if (ageMs > PERSISTED_VALID_MS) return null
  const minutes = Math.max(1, Math.round(ageMs / 60_000))
  if (ageMs >= STALE_ESCALATION_MS) {
    return `Datos de hace ${minutes} minutos — conéctate para actualizar`
  }
  return `Datos de hace ${minutes} min`
}
